import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateRecurringBillTemplate,
  RecurringBillsGenerateResult,
  RecurringBillTemplateWithLines,
  UpdateRecurringBillTemplate,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// AP Phase C4 — Recurring vendor-bill template hooks.
// Templates drive a daily cron that materialises one PENDING ApBill per due
// period (createBill postJv=false). The main use case is 209-branch rent/utilities.
// Cache is keyed by companyId; all mutating operations invalidate both the
// template list and the AP-bills list (generate creates PENDING bills).

const listKey = (companyId: string | undefined) => ['recurring-bill-templates', companyId] as const;

// List all templates for the company (with lines — the API returns the full shape).
export function useRecurringTemplates(companyId: string | undefined) {
  return useQuery({
    queryKey: listKey(companyId),
    queryFn: () =>
      api<RecurringBillTemplateWithLines[]>(`/companies/${companyId}/ap/recurring-bills`),
    enabled: !!companyId,
  });
}

// Single-template detail (header + lines).
export function useRecurringTemplate(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['recurring-bill-template', companyId, id] as const,
    queryFn: () =>
      api<RecurringBillTemplateWithLines>(`/companies/${companyId}/ap/recurring-bills/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Invalidate both the template list and the AP bills list for the company.
function invalidateTemplates(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: ['recurring-bill-templates', companyId] });
}

function invalidateApBills(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: ['ap-bills', companyId] });
}

export function useCreateRecurringTemplate(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRecurringBillTemplate) =>
      apiJson<CreateRecurringBillTemplate, RecurringBillTemplateWithLines>(
        'POST',
        `/companies/${companyId}/ap/recurring-bills`,
        data,
      ),
    onSuccess: () => invalidateTemplates(qc, companyId),
  });
}

export function useUpdateRecurringTemplate(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRecurringBillTemplate }) =>
      apiJson<UpdateRecurringBillTemplate, RecurringBillTemplateWithLines>(
        'PATCH',
        `/companies/${companyId}/ap/recurring-bills/${id}`,
        data,
      ),
    onSuccess: () => invalidateTemplates(qc, companyId),
  });
}

// Soft-stop — DELETE flips isActive=false on the server (no records removed).
export function useDeactivateRecurringTemplate(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, RecurringBillTemplateWithLines>(
        'DELETE',
        `/companies/${companyId}/ap/recurring-bills/${id}`,
      ),
    onSuccess: () => invalidateTemplates(qc, companyId),
  });
}

// Manual-trigger: POST .../generate materialises all due templates as PENDING
// AP bills. Also invalidates the AP bills cache so the รออนุมัติ tab refreshes.
export function useGenerateRecurringDue(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<Record<string, never>, RecurringBillsGenerateResult>(
        'POST',
        `/companies/${companyId}/ap/recurring-bills/generate`,
        {},
      ),
    onSuccess: () => {
      invalidateTemplates(qc, companyId);
      invalidateApBills(qc, companyId);
    },
  });
}
